import React, { useEffect, useState } from 'react';
import EmptyInfo from './EmtyInfo';
import { Tabs, Tooltip } from 'antd';
import type { TabsProps } from 'antd';
import IconInfo from '../ui/icons/IconInfo';
import Description from './description';
import useSWR from 'swr';
import { api, HydraData } from '../../api/apiProvider';
import urls from '../../api/urls';
import { useTranslation } from 'react-i18next';
import IconComments from '../ui/icons/IconComments';
import Comments from './comments';
import IconReport from '../ui/icons/IconReport';
import Report from './report';
import BriefStages from './stages';
import IconInfoStages from '../ui/icons/IconInfoStages';
import Roles from '../../roles/Roles';

interface Callable {
  (): void,
}

interface InformationProps {
  briefId: number,
  commentaryId: number,
  updateList: Callable,
  showStages: boolean,
}

export default function Information ({
  briefId = 0,
  commentaryId = 0,
  updateList,
  showStages = false
}: InformationProps) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('description');
  const [tabItems, setTabItems] = useState<TabsProps['items']>([]);

  const getParamsString = () => {
    const params = new URLSearchParams([
      ['t[]', 'brief'], ['t[]', 'task:short'], ['t[]', 'dict:product'], ['t[]', 'product:company'], ['t[]', 'dict'],
      ['t[]', 'company'], ['t[]', 'company:pool'], ['t[]', 'pool'], ['t[]', 'brief:criterion'], ['t[]', 'comment:read'],
      ['t[]', 'brief:engine'], ['t[]', 'engine'],
    ]);

    return params.toString();
  };

  const {
    data: brief,
    mutate,
  } = useSWR<HydraData>(briefId > 0 ? `${urls.briefs}/${briefId}?${getParamsString()}` : null, api.get, {
    revalidateOnFocus: false
  });

  useEffect(() => {
    const items = [];
    if (briefId) {
      items.push({
        key: 'description',
        label: activeTab === 'description' ? t('info.briefDetails') : '',
        children: brief && brief.data ? <Description brief={brief.data} getBrief={() => {
          mutate();
          updateList();
        }}/> : '',
        icon: <Tooltip title={t('info.briefDetails')}> <IconInfo/></Tooltip>,
      });
    }

    if (commentaryId && Roles.hasAccess('canReadBriefDetailsComments')) {
      items.push({
        key: 'comments',
        label: activeTab === 'comments' ? t('info.comments') : '',
        children: <Comments parentCommentId={commentaryId || 0}/>,
        icon: <Tooltip title={t('info.comments')}> <IconComments/></Tooltip>,
      });
    }

    if (brief?.data?.engine?.id && Roles.hasAccess('canReadOptimization')) {
      items.push({
        key: 'report',
        label: activeTab === 'report' ? t('info.report') : '',
        children: <Report
          briefId={briefId}
          engine={brief.data.engine}
        />,
        icon: <Tooltip title={t('info.report')}> <IconReport/></Tooltip>,
      });
    }

    if (showStages) {
      items.push({
        key: 'stages',
        label: activeTab === 'stages' ? t('info.stages') : '',
        children: <BriefStages/>,
        icon: <Tooltip title={t('info.stages')}> <IconInfoStages/></Tooltip>,
      });
    }

    setTabItems(items);
  }, [activeTab, brief, commentaryId]);

  if (!briefId && commentaryId > 0 && activeTab !== 'comments') {
    setActiveTab('comments');
  }

  const onChange = (key: string) => {
    setActiveTab(key);
  };

  return (
    <>
      {briefId === 0 && commentaryId === 0 && !((brief && brief.data && brief.data.engine)) &&
        <EmptyInfo/>
      }

      {(briefId > 0 || commentaryId > 0 || (brief && brief.data && brief.data.engine)) &&
        <Tabs
          defaultActiveKey="description"
          items={tabItems}
          indicator={{ size: (origin) => origin + 20, align: 'center' }}
          style={{ height: '100%' }}
          onChange={onChange}
          size={'small'}
        />
      }
    </>
  );
}
